/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   UTILS / RECEIPT-PDF — Beleg-HTML → PDF-Anhang (Base64)
   =============================================================================
   S93-C (Session 93) — Option A: clientseitige PDF-Erzeugung mit jsPDF +
   html2canvas (kein Fremdanbieter, keine Kundendaten an Dritte, EINE Layout-
   Quelle = das bestehende Standalone-Beleg-HTML).

   Verwendung:
     const base64 = await receiptHtmlToPdfBase64(standaloneHtml, { widthMm: 80 });
     // → reiner Base64 OHNE `data:`-Prefix, direkt als Resend-Attachment-`content`.

   Quellen-HTML (Single-Source, KEIN Layout-Duplikat):
     - Z-Bon:     buildStandaloneZBonHtmlFresh()  (day-close-zbon-action.js)
     - Kassenbon: buildStandaloneReceiptHtml()     (orders-receipt-actions.js)

   Wichtig:
     - jsPDF + html2canvas werden LAZY importiert (nicht in den Main-Bundle).
     - Logo muss als Data-URL vorliegen (inlineBrandLogoForEmail), sonst taintet
       es das Canvas (cross-origin) und html2canvas wirft. Der Kassenbon-TSE-QR
       ist im Quell-HTML bereits als SVG inlined (buildStandaloneReceiptHtml).
     - Raster-PDF (Text nicht durchsuchbar) — bewusste Entscheidung (Jonas S93-C).
   ============================================================================ */

/**
 * S213-A — Storage-Pfad eines dauerhaft abgelegten Bon-PDFs im privaten Bucket
 * `bon-pdfs`. Kanonisches Schema `{restaurant_id}/{order_id}.pdf` — der erste
 * Pfad-Segment MUSS die restaurant_id sein, weil die Storage-RLS darüber den
 * Tenant-Scope prüft (`storage.foldername(name)[1] = current_restaurant_id()`).
 *
 * Pure (Vitest). Wirft bei fehlendem Tenant/Order, damit ein leerer Präfix nie
 * versehentlich einen fremden/globalen Pfad erzeugt (RLS-Sicherheit).
 *
 * @param {string} restaurantId  z. B. 'rest_default'
 * @param {string} orderId       z. B. 'ord_ab12…'
 * @returns {string} `${restaurantId}/${orderId}.pdf`
 */
export function bonPdfStoragePath(restaurantId, orderId) {
  const rid = String(restaurantId || '').trim();
  const oid = String(orderId || '').trim();
  if (!rid) throw new Error('bonPdfStoragePath: restaurantId fehlt');
  if (!oid) throw new Error('bonPdfStoragePath: orderId fehlt');
  return `${rid}/${oid}.pdf`;
}

/**
 * Extrahiert `<style>`-CSS + `<body>`-Inhalt aus einem Standalone-HTML-Dokument,
 * damit wir sie in einen Off-Screen-Container injizieren können (html2canvas
 * rendert einen DOM-Knoten, kein komplettes Dokument).
 */
export function _splitStandaloneHtml(html) {
  const src = String(html || '');
  const styleMatches = src.match(/<style[^>]*>[\s\S]*?<\/style>/gi) || [];
  const css = styleMatches.map(s => s.replace(/<\/?style[^>]*>/gi, '')).join('\n');
  const bodyMatch = src.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  const bodyHtml = bodyMatch ? bodyMatch[1] : src;
  return { css, bodyHtml };
}

/**
 * Rendert ein Standalone-Beleg-HTML in ein einseitiges PDF und liefert dessen
 * Base64 (ohne `data:`-Prefix) — bereit als Resend-Attachment-`content`.
 *
 * @param {string} html  Standalone-HTML (mit <style> + <body>), Logo als Data-URL
 * @param {object} [opts]
 * @param {number} [opts.widthMm=80]   PDF-Seitenbreite in mm (80-mm-Bonlayout)
 * @param {number} [opts.scale=2]      html2canvas-Scale (Schärfe; 2 erhält die
 *                                     QR-Scanbarkeit auch nach JPEG-Kompression)
 * @param {number} [opts.quality=0.9]  JPEG-Qualität (0..1). JPEG statt PNG senkt
 *                                     die Dateigröße eines weiß-dominierten Belegs
 *                                     drastisch; 0.9 @ scale 2 hält den TSE-QR scanbar.
 * @param {number} [opts.renderWidthPx=560] Off-Screen-Render-Breite in px
 * @param {number} [opts.heightMm]     S264 — feste Seitenhöhe (mit widthMm eine
 *                                     feste Seite, z. B. A4 quer 297×210); das
 *                                     Bild wird eingepasst und zentriert. Ohne
 *                                     Angabe: Endlos-Bon wie bisher.
 * @returns {Promise<string>} Base64-String (ohne `data:`-Prefix)
 * @throws wenn html2canvas/jsPDF nicht verfügbar sind oder das Rendern fehlschlägt
 */
export async function receiptHtmlToPdfBase64(html, opts = {}) {
  const widthMm = opts.widthMm || 80;
  const scale = opts.scale || 2;
  const quality = (typeof opts.quality === 'number') ? opts.quality : 0.9;
  const renderWidthPx = opts.renderWidthPx || 560;

  if (typeof document === 'undefined') {
    throw new Error('receiptHtmlToPdfBase64: kein DOM verfügbar');
  }

  const { css, bodyHtml } = _splitStandaloneHtml(html);

  // PDF-only-CSS: das Screen-Komfort-Akkordeon „Klartext-Payload für Audit"
  // (<details class="receipt-tse-payload-details">) wird im PDF ausgeblendet —
  // html2canvas fing es aufgeklappt ein und es überlappte den Fußbereich
  // (§147-AO-Hinweis + Disclaimer). QR-Code + Signaturzähler-Tabelle bleiben auf
  // dem Beleg, der Klartext-Payload ist redundant. Z-Bon hat kein <details> → no-op.
  const pdfOnlyCss = '.receipt-tse-payload-details{display:none !important;}';

  // S230 (Ticket 4) — Weiß-Blitz-Fix: NICHT mehr per host.innerHTML in den Haupt-
  // DOM injizieren. Das Standalone-Bon-<style> enthält eine globale `body{...}`-
  // Regel (S183 helle Papier-Insel, color-scheme:light) — als <style> im Haupt-
  // Dokument wirkt sie GLOBAL und kippt das ganze Cockpit für die Capture-Dauer
  // (~2 s) auf hell (per A/B-Repro bewiesen). Lösung: ein verstecktes same-origin
  // iframe rendert den Bon isoliert (Style-Isolation by design — kein Leak in
  // beide Richtungen). html2canvas rendert Elemente fremder same-origin-Dokumente.
  // WICHTIG: body inline `padding:0 !important`, sonst schmälert das Bon-eigene
  // `body{padding:24px}` die Karte (1024 statt 1120 px Canvas-Breite).
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText =
    `position:fixed;left:-10000px;top:0;width:${renderWidthPx}px;height:10px;visibility:hidden;border:0`;
  document.body.appendChild(frame);

  try {
    const idoc = frame.contentDocument || (frame.contentWindow && frame.contentWindow.document);
    if (!idoc) throw new Error('receiptHtmlToPdfBase64: iframe-Dokument nicht verfügbar');
    idoc.open();
    idoc.write(
      `<!DOCTYPE html><html><head><meta charset="utf-8"><style>${css}\n${pdfOnlyCss}</style></head>` +
      `<body style="margin:0 !important;padding:0 !important;background:#ffffff">` +
      `<div class="gg-pdf-body" style="background:#ffffff">${bodyHtml}</div>` +
      `</body></html>`
    );
    idoc.close();

    // Settlen: Bilder (Logo-Data-URL) dekodieren + Web-Fonts (Schreibschrift)
    // laden, damit html2canvas den fertigen Bon einfängt. Beides nicht-fatal —
    // fehlende API/Decode-Fehler blockieren die Erzeugung nicht.
    try {
      const imgs = Array.from(idoc.querySelectorAll('img'));
      await Promise.all(imgs.map(img =>
        (typeof img.decode === 'function') ? img.decode().catch(() => {}) : Promise.resolve()));
    } catch (_) { /* decode nicht unterstützt → weiter */ }
    try {
      if (idoc.fonts && idoc.fonts.ready) await idoc.fonts.ready;
    } catch (_) { /* Font-Loading-API fehlt → weiter */ }

    const [{ default: html2canvas }, jsPdfMod] = await Promise.all([
      import('html2canvas'),
      import('jspdf')
    ]);
    const JsPDF = jsPdfMod.jsPDF || jsPdfMod.default;

    const target = idoc.querySelector('.gg-pdf-body') || idoc.body;
    const canvas = await html2canvas(target, {
      scale,
      backgroundColor: '#ffffff',
      useCORS: true,
      logging: false
    });

    const cw = canvas.width || 1;
    const ch = canvas.height || 1;

    if (opts.heightMm) {
      // S264 — feste Seite (A4 quer für den Dienstplan: widthMm 297, heightMm 210).
      // Das Bild wird proportional in die Seite eingepasst und zentriert; Bestands-
      // aufrufer (Bon 80 mm, Z-Bon) laufen weiter über den Endlos-Pfad unten.
      const pageW = widthMm;
      const pageH = opts.heightMm;
      let imgW = pageW;
      let imgH = (ch / cw) * imgW;
      if (imgH > pageH) { imgH = pageH; imgW = (cw / ch) * imgH; }
      const pdf = new JsPDF({
        unit: 'mm', format: [pageW, pageH], orientation: pageW > pageH ? 'landscape' : 'portrait'
      });
      pdf.addImage(canvas.toDataURL('image/jpeg', quality), 'JPEG',
        (pageW - imgW) / 2, (pageH - imgH) / 2, imgW, imgH);
      const dataUri = pdf.output('datauristring');
      const comma = dataUri.indexOf(',');
      return comma >= 0 ? dataUri.slice(comma + 1) : dataUri;
    }

    const heightMm = (ch / cw) * widthMm;

    const pdf = new JsPDF({ unit: 'mm', format: [widthMm, heightMm], orientation: 'portrait' });
    // JPEG statt PNG: weiß-dominierter Beleg komprimiert um ein Vielfaches
    // (~7 MB → ~1 MB), QR bleibt bei scale 2 / q0.9 scanbar.
    pdf.addImage(canvas.toDataURL('image/jpeg', quality), 'JPEG', 0, 0, widthMm, heightMm);

    // datauristring → 'data:application/pdf;filename=…;base64,XXXX' bzw. '…base64,XXXX'
    const dataUri = pdf.output('datauristring');
    const comma = dataUri.indexOf(',');
    return comma >= 0 ? dataUri.slice(comma + 1) : dataUri;
  } finally {
    frame.remove();
  }
}
